import { createClient } from '@supabase/supabase-js'
import { jsPDF } from 'jspdf'
import { t, setLanguage, updateTranslations, initI18n } from '../i18n.js'
import { escapeHtml, formatDate, maintenanceStatus, addDays } from '../utils.js'
import { initTelegramLink } from '../telegram-link.js'

// ==================== CONFIGURATION ====================
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseKey) {
  console.error('Missing Supabase environment variables!')
}

// Session token is injected into every Supabase request so RLS policies
// can identify the authenticated tenant without Supabase Auth.
// Pre-login queries (nfc_tags, equipment, tenants) work without a token.
let sessionToken = sessionStorage.getItem('kixik_mechanic_token') || null

const supabase = createClient(supabaseUrl, supabaseKey, {
  global: {
    fetch: (url, options = {}) => {
      if (sessionToken) {
        const headers = new Headers(options.headers)
        headers.set('x-session-token', sessionToken)
        options.headers = headers
      }
      return fetch(url, options)
    },
  },
})

// ==================== STATE ====================
let currentEquipment = null
let currentMechanic = null
let currentChecklist = null
let urlCode = null
let tempPassword = null
let workLogsMap = {}
let activeServiceRequest = null

// Act / signature state
let pendingActData = null     // { id, workLog } — set after work log insert
let mechanicSigDataUrl = null
let clientSigDataUrl = null

// ==================== DOM ELEMENTS ====================
const loadingScreen = document.getElementById('loadingScreen')
const errorScreen = document.getElementById('errorScreen')
const loginScreen = document.getElementById('loginScreen')
const mainScreen = document.getElementById('mainScreen')

const errorCode = document.getElementById('errorCode')
const equipmentPreview = document.getElementById('equipmentPreview')
const loginForm = document.getElementById('loginForm')
const loginError = document.getElementById('loginError')
const loginBtnText = document.getElementById('loginBtnText')

const logoutBtn = document.getElementById('logoutBtn')
const mechanicName = document.getElementById('mechanicName')
const auditBtn = document.getElementById('auditBtn')
const specialistToggleBtn = document.getElementById('specialistToggleBtn')
const equipmentCard = document.getElementById('equipmentCard')
const equipmentName = document.getElementById('equipmentName')
const equipmentType = document.getElementById('equipmentType')
const equipmentLocation = document.getElementById('equipmentLocation')
const equipmentSerial = document.getElementById('equipmentSerial')
const equipmentStatus = document.getElementById('equipmentStatus')

const toggleHistory = document.getElementById('toggleHistory')
const toggleHistoryText = document.getElementById('toggleHistoryText')
const workHistory = document.getElementById('workHistory')
const historyContent = document.getElementById('historyContent')

const addWorkLogForm = document.getElementById('addWorkLogForm')
const workDateInput = document.getElementById('workDate')
const submitError = document.getElementById('submitError')
const submitBtnText = document.getElementById('submitBtnText')
const checklistSection = document.getElementById('checklistSection')
const checklistItems = document.getElementById('checklistItems')
// Change password form submit
const changePasswordForm = document.getElementById('changePasswordForm')
const changePasswordError = document.getElementById('changePasswordError')
const changePasswordBtnText = document.getElementById('changePasswordBtnText')

changePasswordForm?.addEventListener('submit', async (e) => {
  e.preventDefault()
  
  const newPassword = document.getElementById('newPassword').value
  const confirmPassword = document.getElementById('confirmPassword').value
  
  if (newPassword !== confirmPassword) {
    changePasswordError.textContent = t('error.passwordsDoNotMatch')
    changePasswordError.classList.add('show')
    return
  }
  
  if (newPassword.length < 8) {
    changePasswordError.textContent = t('error.passwordTooShort')
    changePasswordError.classList.add('show')
    return
  }
  
  changePasswordBtnText.textContent = t('common.saving')
  changePasswordForm.querySelector('button').disabled = true
  changePasswordError.classList.remove('show')
  
  try {
    const { data, error } = await supabase.functions.invoke('auth-change-password', {
      body: { user_id: currentMechanic.userId, new_password: newPassword, type: 'user' }
    })

    if (error) throw new Error(error.message)
    if (data?.error) throw new Error(data.error)

    // Update session
    currentMechanic.must_change_password = false
    sessionStorage.setItem('kixik_mechanic', JSON.stringify(currentMechanic))
    
    alert(t('alert.passwordChanged'))
    showMainScreen()
    
  } catch (error) {
    console.error('Error changing password:', error)
    changePasswordError.textContent = error.message
    changePasswordError.classList.add('show')
  } finally {
    changePasswordBtnText.textContent = t('auth.changePassword')
    changePasswordForm.querySelector('button').disabled = false
  }
})
// ==================== INITIALIZATION ====================
async function init() {
  // Initialize i18n
  await initI18n()
  updateTranslations()
  
  // Setup language switcher
  const languageSelect = document.getElementById('languageSelect')
  if (languageSelect) {
    languageSelect.addEventListener('change', async (e) => {
      await setLanguage(e.target.value)
      updateTranslations()
      updateUI()
    })
  }
  
  // Extract URL code from path
  const path = window.location.pathname
  urlCode = path.split('/e/')[1]
  
  // ✅ ЗАГЛУШКА ДЛЯ PWA: если зашли через иконку без кода в URL
  if (!urlCode) {
    if (loadingScreen) loadingScreen.style.display = 'none'
    if (errorScreen) {
      errorScreen.style.display = 'flex'
      const errorCard = errorScreen.querySelector('.error-card')
      if (errorCard) {
        // Меняем иконку на смартфон
        const errorIcon = errorCard.querySelector('.error-icon')
        if (errorIcon) errorIcon.textContent = '📲'
        
        // Устанавливаем атрибуты для мультиязычности
        const h1 = errorCard.querySelector('h1')
        const p = errorCard.querySelector('p')
        if (h1) h1.setAttribute('data-i18n', 'mechanicPage.scanPromptTitle')
        if (p) {
            p.setAttribute('data-i18n', 'mechanicPage.scanPromptMsg')
            // Прямое обновление текста, если i18n не подтянется сразу
            p.textContent = "Please scan the NFC tag or QRcode on the equipment to start service."
        }
        // Логика кнопки сканера
        const scanBtn = document.getElementById('openScannerBtn');
        scanBtn?.addEventListener('click', () => {
            startQRScanner();
        });

        // Прячем техническую строку "Code: undefined"
        if (errorCode) errorCode.style.display = 'none'
        
        // Запускаем перевод текста
        updateTranslations()
      }
    }
    return // Останавливаем выполнение
  }
  
  // Load equipment by code
  await loadEquipment()
}

// ==================== EQUIPMENT LOADING ====================
async function loadEquipment() {
  try {
    showScreen('loading')
    
    const { data: nfcTag, error } = await supabase
      .from('nfc_tags')
      .select(`
        *,
        equipment:equipment_id (
          *,
          checklist:checklists!equipment_checklist_id_fkey (id, name, sections),
          equipment_type:equipment_types (*),
          building:buildings (id, name, address),
          zone:zones (id, name),
          tenant:tenants (company_name, admin_phone, admin_email, status)
        )
      `)
      .eq('url_code', urlCode)
      .single()

    if (error || !nfcTag) {
      throw new Error('Equipment not found')
    }

    if (!nfcTag.equipment) {
      showError(t('mechanicPage.equipmentNotFoundMsg'))
      return
    }

    currentEquipment = nfcTag.equipment

    // Load assigned specialist separately (no named FK constraint in DB)
    if (currentEquipment.assigned_specialist_id) {
      const { data: specialist } = await supabase
        .from('users')
        .select('id, full_name, email, phone')
        .eq('id', currentEquipment.assigned_specialist_id)
        .single()
      currentEquipment.assigned_specialist = specialist || null
    }

    // Block access if tenant is suspended due to overdue payment
    if (currentEquipment.tenant?.status === 'suspended') {
      showError(t('error.tenantSuspended'))
      return
    }

    // Заполняем блок компании
    const tenant = currentEquipment.tenant
    if (tenant) {
      const companyInfoBlock = document.getElementById('companyInfoBlock')
      const companyNameEl = document.getElementById('companyName')
      const companyPhoneEl = document.getElementById('companyPhone')
      
      if (companyNameEl) companyNameEl.textContent = tenant.company_name || '—'

      // Показываем только телефон админа
      const phoneToShow = tenant.admin_phone

      if (companyPhoneEl && phoneToShow) {
        companyPhoneEl.textContent = '📞 ' + phoneToShow
        companyPhoneEl.href = 'tel:' + phoneToShow.replace(/\s/g, '')
      } else if (companyPhoneEl) {
        companyPhoneEl.style.display = 'none'
      }
      
      if (companyInfoBlock) companyInfoBlock.style.display = 'block'
    }
    
    // Parse checklist from the joined query result (avoids RLS issues with a separate request)
    if (currentEquipment.checklist) {
      const cl = currentEquipment.checklist
      try {
        const sections = typeof cl.sections === 'string'
          ? JSON.parse(cl.sections)
          : (cl.sections || [])
        cl.items = sections.map(s => s.title || s.name || s.text || '').filter(Boolean)
      } catch (e) {
        cl.items = []
      }
      currentChecklist = cl
      console.log('✅ Checklist loaded via join:', cl.name, '— items:', cl.items.length)
    } else {
      currentChecklist = null
      console.log('⚠️ No checklist assigned to this equipment')
    }
    
    // Check for admin passthrough (admin opened this page via equipment dashboard)
    const passthroughRaw = localStorage.getItem('kixik_admin_passthrough')
    if (passthroughRaw) {
      try {
        const pt = JSON.parse(passthroughRaw)
        const age = Date.now() - (pt.timestamp || 0)
        if (age < 60000 && pt.tenantId === currentEquipment.tenant_id) {
          localStorage.removeItem('kixik_admin_passthrough')
          currentMechanic = {
            userId: pt.userId,
            username: pt.username,
            fullName: pt.fullName,
            tenantId: pt.tenantId,
            role: pt.role,
            equipmentId: currentEquipment.id,
            timestamp: Date.now()
          }
          sessionStorage.setItem('kixik_mechanic', JSON.stringify(currentMechanic))
          sessionStorage.setItem('kixik_admin_return', '1')
          if (pt.sessionToken) {
            sessionToken = pt.sessionToken
            sessionStorage.setItem('kixik_mechanic_token', pt.sessionToken)
          }
          showMainScreen()
          return
        }
      } catch (e) {
        localStorage.removeItem('kixik_admin_passthrough')
      }
    }

    const session = checkSession()
    if (session && session.equipmentId === currentEquipment.id) {
      currentMechanic = session
      showMainScreen()
    } else {
      showLoginScreen()
    }
    
  } catch (error) {
    console.error('Error loading equipment:', error)
    showError(error.message)
  }
}

// ==================== CHECKLIST ====================
async function loadChecklist(checklistId) {
  try {
    console.log('🔍 Loading checklist ID:', checklistId)
    
    const { data: checklist, error } = await supabase
      .from('checklists')
      .select('*')
      .eq('id', checklistId)
      .maybeSingle()

    if (error) throw error

    if (!checklist) {
      console.log('⚠️ Checklist not found:', checklistId)
      currentChecklist = null
      return
    }

    console.log('🔍 Checklist loaded:', checklist)
    console.log('🔍 Sections:', checklist.sections)

    // Парсим sections как items
    if (checklist && checklist.sections) {
      try {
        const sections = typeof checklist.sections === 'string'
          ? JSON.parse(checklist.sections)
          : checklist.sections

        // Support both {title} and {name} / {text} formats for legacy data
        checklist.items = sections.map(s => s.title || s.name || s.text || '').filter(Boolean)
        console.log('✅ Checklist items parsed:', checklist.items)
      } catch (e) {
        console.error('❌ Error parsing checklist sections:', e)
        checklist.items = []
      }
    } else {
      console.log('⚠️ No sections in checklist')
      checklist.items = []
    }
    
    currentChecklist = checklist
    console.log('✅ currentChecklist set:', currentChecklist)
    
  } catch (error) {
    console.error('❌ Error loading checklist:', error)
    currentChecklist = null
  }
}
function showChangePasswordScreen() {
  showScreen('changePassword')
  document.getElementById('currentPassword').value = tempPassword
  document.getElementById('currentPassword').readOnly = true
}
function renderChecklist() {
  console.log('🔍 renderChecklist called')
  console.log('🔍 checklistSection exists:', !!checklistSection)
  console.log('🔍 checklistItems exists:', !!checklistItems)
  console.log('🔍 currentChecklist:', currentChecklist)
  
  if (!checklistSection || !checklistItems) {
    console.log('❌ Checklist elements not found in DOM')
    return
  }
  
  if (!currentChecklist || !currentChecklist.items || currentChecklist.items.length === 0) {
    console.log('⚠️ No checklist or items to render')
    checklistSection.style.display = 'none'
    return
  }
  
  console.log('✅ Rendering', currentChecklist.items.length, 'checklist items')
  checklistSection.style.display = 'block'
  
  const html = currentChecklist.items.map((item, index) => `
    <div class="checklist-item">
      <label>
        <input 
          type="checkbox" 
          name="checklist-item-${index}" 
          value="${item}"
        >
        <span>${item}</span>
      </label>
    </div>
  `).join('')
  
  console.log('🔍 Generated HTML:', html)
  checklistItems.innerHTML = html
  console.log('✅ Checklist rendered!')
}

function getChecklistData() {
  if (!currentChecklist) return null
  
  const checkboxes = document.querySelectorAll('#checklistItems input[type="checkbox"]:checked')
  const completedItems = Array.from(checkboxes).map(cb => cb.value)
  
  if (completedItems.length === 0) return null
  
  return {
    checklist_id: currentChecklist.id,
    completed_items: completedItems,
    completed_count: completedItems.length,
    total_count: currentChecklist.items.length
  }
}

// ==================== SESSION MANAGEMENT ====================
function checkSession() {
  const sessionData = sessionStorage.getItem('kixik_mechanic')
  if (!sessionData) return null
  
  try {
    return JSON.parse(sessionData)
  } catch {
    return null
  }
}

function saveSession(mechanic, token = null) {
  const sessionData = {
    userId: mechanic.id,
    username: mechanic.username,
    fullName: mechanic.full_name,
    tenantId: mechanic.tenant_id,
    role: mechanic.role,
    equipmentId: currentEquipment.id,
    timestamp: new Date().toISOString()
  }
  sessionStorage.setItem('kixik_mechanic', JSON.stringify(sessionData))
  currentMechanic = sessionData
  if (token) {
    sessionToken = token
    sessionStorage.setItem('kixik_mechanic_token', token)
  }
}

function clearSession() {
  sessionStorage.removeItem('kixik_mechanic')
  sessionStorage.removeItem('kixik_mechanic_token')
  currentMechanic = null
  sessionToken = null
}

// ==================== AUTHENTICATION ====================
loginForm.addEventListener('submit', async (e) => {
  e.preventDefault()

  const username = document.getElementById('username').value.trim()
  const password = document.getElementById('password').value

  await login(username, password)
})

async function login(username, password) {
  loginBtnText.textContent = t('auth.loggingIn')
  loginForm.querySelector('button').disabled = true
  loginError.classList.remove('show')

  try {
    const companyName = currentEquipment.tenant?.company_name
    let authUser = null

    // Режим Аудитора: пароль пустой и username совпадает с названием компании
    let loginToken = null
    if (!password) {
      if (username.toLowerCase() === companyName?.toLowerCase()) {
        authUser = {
          id: 'auditor-' + currentEquipment.tenant_id,
          username: username,
          full_name: `Auditor (${username})`,
          role: 'auditor',
          tenant_id: currentEquipment.tenant_id
        }
      } else {
        throw new Error(t('auth.auditorWrongCompany') || 'Company name does not match. Enter the exact company name for auditor access.')
      }
    } else {
      // Стандартный вход для специалистов (с паролем)
      const { data, error } = await supabase.functions.invoke('auth-login', {
        body: {
          username,
          password,
          type: 'mechanic',
          tenant_id: currentEquipment.tenant_id
        }
      })

      if (error) throw new Error(error.message)
      if (data?.error) throw new Error(t('error.invalidCredentials'))

      authUser = data.user
      loginToken = data.sessionToken
    }

    saveSession(authUser, loginToken)

    if (authUser.must_change_password && authUser.role !== 'auditor') {
      tempPassword = password
      showChangePasswordScreen()
    } else {
      showMainScreen()
    }
  } catch (error) {
    loginError.textContent = error.message
    loginError.classList.add('show')
  } finally {
    loginBtnText.textContent = t('auth.login')
    loginForm.querySelector('button').disabled = false
  }
}
logoutBtn.addEventListener('click', () => {
  supabase.functions.invoke('auth-logout').catch(() => {}) // invalidate token server-side
  clearSession()
  showLoginScreen()
})

// Back to admin — visible only when opened via admin passthrough; returns immediately
document.getElementById('backToAdminBtn')?.addEventListener('click', () => {
  sessionStorage.removeItem('kixik_admin_return')
  window.location.href = '/equipment.html'
})

// ==================== ACTION BUTTONS ====================
auditBtn?.addEventListener('click', () => {
  const authUser = {
    id: 'auditor-' + currentEquipment.tenant_id,
    username: 'auditor',
    full_name: t('mechanicPage.auditorName') || 'Auditor',
    role: 'auditor',
    tenant_id: currentEquipment.tenant_id
  }
  saveSession(authUser, null)
  showMainScreen()
})

specialistToggleBtn?.addEventListener('click', () => {
  const isVisible = loginForm.style.display !== 'none'
  loginForm.style.display = isVisible ? 'none' : 'block'
  if (!isVisible) {
    loginForm.querySelector('input')?.focus()
  }
})

// ==================== WORK HISTORY ====================
toggleHistory.addEventListener('click', () => {
  const isVisible = workHistory.style.display !== 'none'
  workHistory.style.display = isVisible ? 'none' : 'block'
  toggleHistoryText.textContent = isVisible 
    ? t('mechanicPage.viewDetails') 
    : t('mechanicPage.hideDetails')
})

async function loadWorkHistory() {
  try {
    const { data, error } = await supabase
      .from('service_history')
      .select(`
        *,
        mechanic:users!service_history_mechanic_id_fkey (full_name, username)
      `)
      .eq('equipment_id', currentEquipment.id)
      .order('work_date', { ascending: false })
      .limit(20)
    
    if (error) throw error
    
    renderWorkHistory(data || [])
    
  } catch (error) {
    console.error('Error loading work history:', error)
    historyContent.innerHTML = `<p class="error">${t('common.loadingFailed')}</p>`
  }
}

function renderWorkHistory(logs) {
  if (!logs || logs.length === 0) {
    historyContent.innerHTML = `<p class="no-history" data-i18n="mechanicPage.noHistory">${t('mechanicPage.noHistory')}</p>`
    return
  }

  workLogsMap = {}
  logs.forEach(log => { workLogsMap[log.id] = log })

  const historyHTML = logs.map(log => {
    // Получаем перевод типа работы
    const workTypeLabel = t(`mechanicPage.${log.work_type}`)
    
    // Форматируем даты
    const workDate = formatDate(log.work_date)
    const nextMaintenanceDate = log.next_maintenance_date ? formatDate(log.next_maintenance_date) : null
    
    // Формируем HTML
    let itemHTML = `
      <div class="history-item" onclick="showWorkLogDetail('${log.id}')" style="cursor:pointer">
        <div class="history-header">
          <span class="history-date">${workDate}</span>
          <span class="history-type ${log.work_type}">${workTypeLabel}</span>
        </div>
        <p class="history-description">${log.description}</p>`
    
    // Добавляем чеклист если есть
    if (log.completed_checklist_items) {
      itemHTML += `
        <p class="history-checklist">✅ ${t('mechanicPage.checklistComplete')}: ${log.completed_checklist_items} ${t('mechanicPage.completedItems')}</p>`
    }
    
    // Добавляем футер
    itemHTML += `
        <div class="history-footer">
          <span class="history-mechanic">
            👤 ${t('mechanicPage.loggedBy')}: ${log.mechanic_name || log.mechanic?.full_name || log.mechanic?.username || '-'}
          </span>`
    
    // Добавляем следующее обслуживание если есть
    if (nextMaintenanceDate) {
      itemHTML += `
          <span class="history-next">
            📅 ${t('mechanicPage.nextMaintenance')}: ${nextMaintenanceDate}
          </span>`
    }

    // Добавляем моточасы если есть
    if (log.engine_hours != null) {
      itemHTML += `
          <span class="history-engine-hours">
            ⏱ ${t('mechanicPage.engineHoursHistory').replace('{hours}', log.engine_hours)}
          </span>`
    }

    if (log.act_pdf_url) {
      itemHTML += `
          <div class="history-act-link">
            <a href="${escapeHtml(log.act_pdf_url)}" target="_blank" class="detail-act-link">
              📄 ${t('act.openAct')}
            </a>
          </div>`
    }

    itemHTML += `
        </div>
      </div>`

    return itemHTML
  }).join('')
  
  historyContent.innerHTML = historyHTML
}

async function loadOpenServiceRequest() {
  try {
    const { data } = await supabase
      .from('service_requests')
      .select('id, problem_description, status, requester_name')
      .eq('equipment_id', currentEquipment.id)
      .in('status', ['new', 'assigned', 'in_progress'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    activeServiceRequest = data || null

    const banner = document.getElementById('openRequestBanner')
    const closeOption = document.getElementById('closeRequestOption')

    if (activeServiceRequest && banner) {
      document.getElementById('openRequestTitle').textContent = t('mechanicPage.openRequestTitle')
      document.getElementById('openRequestDesc').textContent = activeServiceRequest.problem_description
      banner.style.display = 'block'
      if (closeOption) closeOption.style.display = 'block'
    } else if (banner) {
      banner.style.display = 'none'
      if (closeOption) closeOption.style.display = 'none'
    }
  } catch (error) {
    activeServiceRequest = null
  }
}

window.showWorkLogDetail = function(logId) {
  const log = workLogsMap[logId]
  if (!log) return

  const workDate = formatDate(log.work_date)
  const workTypeLabel = t(`mechanicPage.${log.work_type}`)
  const mechanicName = log.mechanic_name || log.mechanic?.full_name || log.mechanic?.username || '-'
  const nextDate = log.next_maintenance_date ? formatDate(log.next_maintenance_date) : null

  document.getElementById('workLogDetailTitle').textContent = `${workDate} — ${workTypeLabel}`

  let html = `<div class="work-log-detail">`

  if (log.work_items && log.work_items.length > 0) {
    html += `<div class="detail-section"><ul class="detail-work-items">`
    log.work_items.forEach(item => {
      const hrs = item.hours ? ` — ${item.hours} ${t('mechanicPage.workItemHoursPlaceholder')}` : ''
      html += `<li>${escapeHtml(item.name)}${escapeHtml(hrs)}</li>`
    })
    html += `</ul></div>`
  }

  if (log.description) {
    html += `<div class="detail-section">
      <p class="detail-description">${escapeHtml(log.description)}</p>
    </div>`
  }

  if (log.checklist_data) {
    try {
      const items = JSON.parse(log.checklist_data)
      if (items && items.length > 0) {
        html += `<div class="detail-section detail-checklist">
          <h4>✅ ${t('mechanicPage.checklistComplete')}</h4>
          <ul class="detail-checklist-list">
            ${items.map(item => `<li>${escapeHtml(item)}</li>`).join('')}
          </ul>
        </div>`
      }
    } catch (e) {}
  }

  html += `<div class="detail-footer">
    <span>👤 ${t('mechanicPage.loggedBy')}: ${escapeHtml(mechanicName)}</span>
    ${nextDate ? `<span>📅 ${t('mechanicPage.nextMaintenance')}: ${nextDate}</span>` : ''}
  </div>`

  if (log.act_pdf_url) {
    html += `<div style="margin-top:0.75rem;">
      <a href="${escapeHtml(log.act_pdf_url)}" target="_blank" class="detail-act-link">
        📄 ${t('act.openAct')}
      </a>
    </div>`
  }

  html += `</div>`

  document.getElementById('workLogDetailContent').innerHTML = html
  document.getElementById('workLogDetailModal').style.display = 'flex'
}

// ==================== WORK ITEMS ====================
let workItemIndex = 0

function createWorkItemRow() {
  const idx = workItemIndex++
  const row = document.createElement('div')
  row.className = 'work-item-row'
  row.dataset.idx = idx

  const nameInput = document.createElement('input')
  nameInput.type = 'text'
  nameInput.className = 'work-item-name'
  nameInput.required = false
  nameInput.placeholder = t('mechanicPage.workItemNamePlaceholder')

  const hoursInput = document.createElement('input')
  hoursInput.type = 'number'
  hoursInput.className = 'work-item-hours'
  hoursInput.min = '0.5'
  hoursInput.step = '0.5'
  hoursInput.placeholder = t('mechanicPage.workItemHoursPlaceholder')

  const removeBtn = document.createElement('button')
  removeBtn.type = 'button'
  removeBtn.className = 'work-item-remove'
  removeBtn.textContent = '×'
  removeBtn.addEventListener('click', () => {
    const list = document.getElementById('workItemsList')
    if (list.children.length > 1) row.remove()
  })

  row.appendChild(nameInput)
  row.appendChild(hoursInput)
  row.appendChild(removeBtn)
  return row
}

function initWorkItems() {
  const list = document.getElementById('workItemsList')
  list.innerHTML = ''
  workItemIndex = 0
  list.appendChild(createWorkItemRow())
}

function getWorkItems() {
  const rows = document.querySelectorAll('#workItemsList .work-item-row')
  return Array.from(rows).map(row => ({
    name: row.querySelector('.work-item-name')?.value?.trim() || '',
    hours: parseFloat(row.querySelector('.work-item-hours')?.value) || null
  })).filter(item => item.name)
}

document.getElementById('addWorkItemBtn')?.addEventListener('click', () => {
  document.getElementById('workItemsList').appendChild(createWorkItemRow())
})

// ==================== ADD WORK LOG ====================
// Set today's date as default
workDateInput.valueAsDate = new Date()
const todayIso = new Date().toISOString().split('T')[0]
const nextMaintenanceInput = document.getElementById('nextMaintenance')
if (nextMaintenanceInput) nextMaintenanceInput.min = todayIso
initWorkItems()

// Show next maintenance date only for ТО (maintenance)
function updateNextMaintenanceVisibility() {
  const workType = document.getElementById('workType')?.value
  const group = document.getElementById('nextMaintenanceGroup')
  if (!group) return
  if (workType === 'maintenance') {
    group.style.display = ''
  } else {
    group.style.display = 'none'
    const input = document.getElementById('nextMaintenance')
    if (input) input.value = ''
  }
}
document.getElementById('workType')?.addEventListener('change', updateNextMaintenanceVisibility)
// Initialize: workType defaults to "maintenance" so field is visible by default
updateNextMaintenanceVisibility()

// ==================== ENGINE HOURS ====================
const ENGINE_HOURS_TYPES = ['industrial', 'vehicles']

function initEngineHoursField() {
  const typeCode = currentEquipment?.equipment_type?.code
  const group = document.getElementById('engineHoursGroup')
  if (!group) return

  if (ENGINE_HOURS_TYPES.includes(typeCode)) {
    group.style.display = ''
    const hint = document.getElementById('engineHoursHint')
    if (hint) {
      // prefer dedicated column, fall back to legacy custom_fields value
      const lastHours = currentEquipment.engine_hours != null
        ? currentEquipment.engine_hours
        : (currentEquipment.custom_fields?.engine_hours ?? null)
      hint.textContent = lastHours != null
        ? t('mechanicPage.engineHoursHint').replace('{hours}', lastHours)
        : ''
    }
  } else {
    group.style.display = 'none'
  }
}

addWorkLogForm.addEventListener('submit', async (e) => {
  e.preventDefault()
  
  submitBtnText.textContent = t('mechanicPage.submitting')
  addWorkLogForm.querySelector('button[type="submit"]').disabled = true
  submitError.classList.remove('show')
  
  try {
    const formData = new FormData(addWorkLogForm)
    
    // Получить данные чеклиста если есть
    const checklistData = getChecklistData()
    
    const shouldCloseRequest = activeServiceRequest &&
      document.getElementById('closeRequestCheckbox')?.checked

    const workItems = getWorkItems()
    const engineHoursRaw = formData.get('engineHours')
    const engineHoursValue = engineHoursRaw ? parseFloat(engineHoursRaw) : null

    const workLog = {
      equipment_id: currentEquipment.id,
      tenant_id: currentEquipment.tenant_id,
      mechanic_id: currentMechanic.userId,
      mechanic_name: currentMechanic.fullName || currentMechanic.username,
      work_date: formData.get('workDate'),
      work_type: formData.get('workType'),
      description: formData.get('description') || '',
      work_items: workItems,
      next_maintenance_date: formData.get('nextMaintenance') || null,
      checklist_id: checklistData ? checklistData.checklist_id : null,
      completed_checklist_items: checklistData ? checklistData.completed_count : null,
      checklist_data: checklistData ? JSON.stringify(checklistData.completed_items) : null,
      service_request_id: shouldCloseRequest ? activeServiceRequest.id : null,
      engine_hours: engineHoursValue
    }

    const { data: insertedLog, error } = await supabase
      .from('service_history')
      .insert([workLog])
      .select('id')
      .single()

    if (error) throw error

    // Close the service request if checkbox was checked
    if (shouldCloseRequest && insertedLog) {
      await supabase
        .from('service_requests')
        .update({
          status: 'done',
          resolved_by_service_history_id: insertedLog.id,
          resolved_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', activeServiceRequest.id)
    }

    // Обновить даты обслуживания и моточасы в equipment
    const equipmentUpdate = { last_maintenance_date: workLog.work_date }

    // A date entered by the specialist always wins. Otherwise roll the
    // EVS 807 maintenance plan forward by the configured interval.
    const rolledForward = workLog.next_maintenance_date
      || addDays(workLog.work_date, currentEquipment.maintenance_interval_days)

    if (rolledForward) {
      equipmentUpdate.next_maintenance_date = rolledForward
    }
    if (engineHoursValue != null) {
      equipmentUpdate.engine_hours = engineHoursValue
      currentEquipment.engine_hours = engineHoursValue
    }
    await supabase
      .from('equipment')
      .update(equipmentUpdate)
      .eq('id', currentEquipment.id)

    // Keep the in-memory copy in sync so the maintenance plan block redraws
    currentEquipment.last_maintenance_date = equipmentUpdate.last_maintenance_date
    if (rolledForward) currentEquipment.next_maintenance_date = rolledForward
    renderMaintenancePlan()

    // Сбросить форму и обновить историю
    addWorkLogForm.reset()
    initWorkItems()
    initEngineHoursField()

    if (currentEquipment?.checklist_id) {
      if (!currentChecklist) await loadChecklist(currentEquipment.checklist_id)
      renderChecklist()
    }

    workDateInput.valueAsDate = new Date()
    const nmi = document.getElementById('nextMaintenance')
    if (nmi) nmi.min = new Date().toISOString().split('T')[0]

    await loadWorkHistory()
    await loadOpenServiceRequest()

    // Show signature/act modal
    pendingActData = { id: insertedLog.id, workLog }
    openSignatureModal()

    alert(t('alert.workLogSaved'))
    
  } catch (error) {
    console.error('Error adding work log:', error)
    submitError.textContent = error.message
    submitError.classList.add('show')
  } finally {
    submitBtnText.textContent = t('mechanicPage.submit')
    addWorkLogForm.querySelector('button[type="submit"]').disabled = false
  }
})

// ==================== UI MANAGEMENT ====================
function showScreen(screen) {
  loadingScreen.style.display = 'none'
  errorScreen.style.display = 'none'
  loginScreen.style.display = 'none'
  changePasswordScreen.style.display = 'none'  // ← ДОБАВЬ
  mainScreen.style.display = 'none'
  
  if (screen === 'loading') {
    loadingScreen.style.display = 'flex'
  } else if (screen === 'error') {
    errorScreen.style.display = 'flex'
  } else if (screen === 'login') {
    loginScreen.style.display = 'flex'
  } else if (screen === 'changePassword') {  // ← ДОБАВЬ
    changePasswordScreen.style.display = 'flex'
  } else if (screen === 'main') {
    mainScreen.style.display = 'block'
  }
}

function showError(message) {
  showScreen('error')
  errorCode.textContent = `Code: ${urlCode}`
  document.querySelector('.error-card h1').textContent = message
}

function showLoginScreen() {
  showScreen('login')
  updateEquipmentPreview()
}

async function showMainScreen() {
  showScreen('main')

  // Show "Back to admin" button when opened via admin passthrough
  const backBtn = document.getElementById('backToAdminBtn')
  if (backBtn) {
    backBtn.style.display = sessionStorage.getItem('kixik_admin_return') ? 'inline-flex' : 'none'
  }

  // Telegram notifications require a real account — auditors (company-name
  // login, no password) have no user_id/session to link.
  const telegramBtn = document.getElementById('telegramBtn')
  if (telegramBtn) {
    if (currentMechanic?.role === 'auditor') {
      telegramBtn.style.display = 'none'
    } else {
      telegramBtn.style.display = ''
      initTelegramLink(supabase)
    }
  }

  // =========================================================
  // УДАЛЕНИЕ ДУБЛЯ (FIX)
  // =========================================================
  if (equipmentPreview) {
    equipmentPreview.style.display = 'none'     // Скрываем
    equipmentPreview.classList.add('hidden')    // Добавляем класс скрытия
    equipmentPreview.innerHTML = ''             // Удаляем HTML внутри, чтобы нечему было отображаться
  }
  // =========================================================

  updateEquipmentCard()
  initEngineHoursField()

  // If checklist wasn't loaded via JOIN (RLS or missing data), load directly
  if (currentEquipment?.checklist_id && !currentChecklist) {
    await loadChecklist(currentEquipment.checklist_id)
  }
  renderChecklist()
  loadWorkHistory()
  loadOpenServiceRequest()

  const addLogSection = document.querySelector('.add-log-section')
  const historySection = document.querySelector('.history-section')
  if (currentMechanic?.role === 'auditor') {
    if (addLogSection) addLogSection.style.display = 'none'
    if (historySection) historySection.style.display = 'none'
    renderAuditorMaintenanceBlock()
  } else {
    if (addLogSection) addLogSection.style.display = ''
    if (historySection) historySection.style.display = ''
  }

  // Просто обновляем имя механика
  if (mechanicName && currentMechanic) {
    mechanicName.textContent = currentMechanic.fullName || currentMechanic.name || currentMechanic.username
  }

}

function updateEquipmentPreview() {
  if (!currentEquipment) return
  
  // Блок оборудования
  equipmentPreview.innerHTML = `
    <div class="info-block-title">${t('equipment.title')}</div>
    <div class="preview-item">
      <span class="preview-label">${t('mechanicPage.equipmentName')}:</span>
      <span class="preview-value"><strong>${currentEquipment.name}</strong></span>
    </div>
    <div class="preview-item">
      <span class="preview-label">${t('mechanicPage.equipmentLocation')}:</span>
      <span class="preview-value">${currentEquipment.location || '-'}</span>
    </div>
    <div class="preview-item">
      <span class="preview-label">${t('common.status')}:</span>
      <span class="preview-value">${currentEquipment.status || 'Active'}</span>
    </div>
  `
}

// ==================== HOOLDUSRAAMAT: LOCATION & MAINTENANCE PLAN ====================

/**
 * "Building › Zone" breadcrumb under the equipment name.
 * Renders nothing when the equipment has no building assigned.
 */
function renderLocationBreadcrumb() {
  document.getElementById('mechLocationBreadcrumb')?.remove()

  const parts = [currentEquipment.building?.name, currentEquipment.zone?.name].filter(Boolean)
  if (parts.length === 0) return

  const el = document.createElement('div')
  el.id = 'mechLocationBreadcrumb'
  el.className = 'location-breadcrumb'
  el.textContent = `📍 ${parts.join(' › ')}`
  equipmentName.after(el)
}

/**
 * Maintenance plan block (EVS 807 hooldusplaan): interval, next due date
 * and a colour-coded status pill.
 */
function renderMaintenancePlan() {
  const detailsContainer = equipmentCard?.querySelector('.equipment-details')
  if (!detailsContainer) return

  detailsContainer.querySelector('.maintenance-plan-block')?.remove()

  const { status, days } = maintenanceStatus(currentEquipment.next_maintenance_date)
  const interval = currentEquipment.maintenance_interval_days

  // Nothing scheduled and no interval — no block at all.
  if (status === 'none' && !interval) return

  const pillLabel = {
    overdue: () => t('maintenance.overdueByDays').replace('{days}', Math.abs(days)),
    soon:    () => t('maintenance.dueInDays').replace('{days}', days),
    ok:      () => t('maintenance.onSchedule'),
    none:    () => t('maintenance.noPlan'),
  }[status]()

  const block = document.createElement('div')
  block.className = 'maintenance-plan-block'
  block.style.cssText = 'margin-top:15px;padding-top:10px;border-top:1px dashed #ccc;'
  block.innerHTML = `
    <div class="detail-row">
      <span class="detail-label">${escapeHtml(t('equipment.nextMaintenanceDate'))}</span>
      <span class="detail-value"><span class="maint-pill ${status}">${escapeHtml(pillLabel)}</span></span>
    </div>
    ${currentEquipment.next_maintenance_date ? `
    <div class="detail-row">
      <span class="detail-label">${escapeHtml(t('maintenance.dueDate'))}</span>
      <span class="detail-value">${escapeHtml(formatDate(currentEquipment.next_maintenance_date))}</span>
    </div>` : ''}
    ${interval ? `
    <div class="detail-row">
      <span class="detail-label">${escapeHtml(t('maintenance.intervalLabel'))}</span>
      <span class="detail-value">${interval} ${escapeHtml(t('maintenance.daysShort'))}</span>
    </div>` : ''}`

  detailsContainer.appendChild(block)
}

function updateEquipmentCard() {
  if (!currentEquipment) return

  // 1. Заполняем базовые текстовые поля (тут дублей не будет, т.к. textContent перезаписывает)
  equipmentName.textContent = currentEquipment.name
  renderLocationBreadcrumb()
  renderMaintenancePlan()
  equipmentType.textContent = currentEquipment.equipment_type?.name || '-'
  equipmentLocation.textContent = currentEquipment.location || '-'
  equipmentSerial.textContent = currentEquipment.serial_number || '-'
  
  if (equipmentStatus) {
    equipmentStatus.textContent = currentEquipment.status || 'active'
    equipmentStatus.className = `status-badge status-${currentEquipment.status || 'active'}`
  }

  // 2. ИСПРАВЛЕНИЕ ДУБЛИРОВАНИЯ ДИНАМИЧЕСКИХ ПОЛЕЙ
  const detailsContainer = equipmentCard.querySelector('.equipment-details')
  
  // !!! ВАЖНО: Сначала ищем и УДАЛЯЕМ старый блок с доп. полями, если он уже есть
  const oldDynamicBlock = detailsContainer.querySelector('.dynamic-passport-info')
  if (oldDynamicBlock) {
    oldDynamicBlock.remove()
  }

  // 3. Генерируем новый HTML
  const schema = currentEquipment.equipment_type?.fields_schema
  const cf = currentEquipment.custom_fields || {}
  const passportFields = schema?.passport || []
  const skipFields = ['name', 'client_company']

  let passportHtml = ''
  passportFields.forEach(field => {
    if (skipFields.includes(field.id)) return
    const val = cf[field.id]
    if (val) passportHtml += `<div class="detail-row"><span class="detail-label">${t('equipment.' + field.id) || field.label}</span><span class="detail-value">${val}</span></div>`
  })

  // 4. Создаем и вставляем новый блок (только если есть данные)
  if (passportHtml) {
    const infoDiv = document.createElement('div')
    infoDiv.className = 'dynamic-passport-info' // Добавляем класс, чтобы потом найти и удалить
    infoDiv.style.marginTop = '15px'
    infoDiv.style.paddingTop = '10px'
    infoDiv.style.borderTop = '1px dashed #ccc'
    infoDiv.innerHTML = passportHtml

    detailsContainer.appendChild(infoDiv)
  }

  // 5. Engine hours — показываем для industrial/vehicles из колонки или custom_fields
  const ENGINE_HOURS_TYPES_CARD = ['industrial', 'vehicles']
  if (ENGINE_HOURS_TYPES_CARD.includes(currentEquipment.equipment_type?.code)) {
    const oldEhBlock = detailsContainer.querySelector('.engine-hours-card-row')
    if (oldEhBlock) oldEhBlock.remove()

    const ehValue = currentEquipment.engine_hours != null
      ? currentEquipment.engine_hours
      : (currentEquipment.custom_fields?.engine_hours ?? null)

    if (ehValue != null) {
      const ehDiv = document.createElement('div')
      ehDiv.className = 'engine-hours-card-row detail-row'
      ehDiv.style.marginTop = '8px'
      ehDiv.innerHTML = `<span class="detail-label">⏱ ${t('equipment.engine_hours')}</span><span class="detail-value">${ehValue} h</span>`
      detailsContainer.appendChild(ehDiv)
    }
  }
}

// ==================== AUDITOR VIEW ====================
function renderAuditorMaintenanceBlock() {
  const detailsContainer = equipmentCard?.querySelector('.equipment-details')
  if (!detailsContainer || !currentEquipment) return

  const old = detailsContainer.querySelector('.auditor-maintenance-block')
  if (old) old.remove()

  const lastM = currentEquipment.last_maintenance_date
    ? new Date(currentEquipment.last_maintenance_date).toLocaleDateString()
    : '—'
  const nextM = currentEquipment.next_maintenance_date
    ? new Date(currentEquipment.next_maintenance_date).toLocaleDateString()
    : '—'
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const isOverdue = currentEquipment.next_maintenance_date
    && new Date(currentEquipment.next_maintenance_date) < today

  const engineHoursTypes = ['industrial', 'vehicles']
  const showEh = engineHoursTypes.includes(currentEquipment.equipment_type?.code)
    && currentEquipment.engine_hours != null
  const ehRow = showEh
    ? `<div class="detail-row"><span class="detail-label">⏱ ${t('equipment.engine_hours')}</span><span class="detail-value">${currentEquipment.engine_hours} h</span></div>`
    : ''

  const specialist = currentEquipment.assigned_specialist
  const specialistRow = specialist?.full_name
    ? `<div class="detail-row"><span class="detail-label">${t('equipment.assignedSpecialist')}</span><span class="detail-value">${escapeHtml(specialist.full_name)}</span></div>`
    : ''

  const block = document.createElement('div')
  block.className = 'auditor-maintenance-block'
  block.style.cssText = 'margin-top:14px;padding-top:12px;border-top:1px solid #e5e7eb'
  block.innerHTML = `
    <div class="detail-row">
      <span class="detail-label">${t('equipment.maintenanceDate')}</span>
      <span class="detail-value">${lastM}</span>
    </div>
    <div class="detail-row">
      <span class="detail-label">${t('equipment.nextMaintenanceDate')}</span>
      <span class="detail-value${isOverdue ? ' overdue-text' : ''}">${nextM}</span>
    </div>
    ${specialistRow}
    ${ehRow}
  `
  detailsContainer.appendChild(block)
}

// ==================== UI UPDATES (FIXED) ====================
function updateUI() {
  // Главный индикатор: если есть текущий механик, значит мы ВНУТРИ системы.
  // В этом случае превью (карточка входа) должна быть мертва.
  const isLoggedIn = !!currentMechanic; 

  if (currentEquipment) {
    if (isLoggedIn) {
      // === МЫ ЗАЛОГИНЕНЫ ===
      // 1. Рисуем большую карточку
      updateEquipmentCard()
      if (equipmentCard) equipmentCard.style.display = 'block'

      // 2. УБИВАЕМ ПРЕВЬЮ НАПОВАЛ
      // Даже если экран логина еще виден (доли секунды), превью там быть не должно
      if (equipmentPreview) {
        equipmentPreview.style.display = 'none'
        equipmentPreview.innerHTML = '' 
      }
      
      // Скрываем сам экран логина на всякий случай
      if (loginScreen) loginScreen.style.display = 'none'
      if (mainScreen) mainScreen.style.display = 'block'

    } else {
      // === МЫ НА ЭКРАНЕ ВХОДА ===
      // 1. Рисуем превью
      updateEquipmentPreview()
      if (equipmentPreview) equipmentPreview.style.display = 'block'

      // 2. Скрываем большую карточку
      if (equipmentCard) equipmentCard.style.display = 'none'
    }
  }

  // История работ
  if (workHistory && workHistory.style.display !== 'none') {
    loadWorkHistory()
  }
}
async function startQRScanner() {
  const reader = document.getElementById('qr-reader');
  const scanBtn = document.getElementById('openScannerBtn');
  
  if (!reader) return;

  if (!window.Html5Qrcode) {
    console.error("html5-qrcode library not loaded");
    alert(t('mechanicPage.scannerUnavailable') || "Scanner could not load. Please check your connection or enter the tag code manually.");
    return;
  }
  
  reader.style.display = 'block';
  if (scanBtn) scanBtn.style.display = 'none';

  const html5QrCode = new Html5Qrcode("qr-reader");
  let isProcessing = false; // Предохранитель от двойного срабатывания

  const qrCodeSuccessCallback = (decodedText) => {
    if (isProcessing) return;
    
    if (decodedText.includes('/e/')) {
      isProcessing = true;
      window.location.href = decodedText;
    }
  };

  const config = { 
    fps: 10, 
    qrbox: { width: 250, height: 250 }
  };

  try {
    await html5QrCode.start(
      { facingMode: "environment" },
      config,
      qrCodeSuccessCallback
    );
  } catch (err) {
    console.error("Start error:", err);
    alert(t('error.cameraAccessDenied') || "Camera error");
    reader.style.display = 'none';
    if (scanBtn) scanBtn.style.display = 'block';
  }
}
// ==================== SIGNATURE & ACT ====================

function initSignatureCanvas(canvas) {
  const ctx = canvas.getContext('2d')
  const wrapper = canvas.parentElement

  function resize() {
    const dpr = window.devicePixelRatio || 1
    const rect = wrapper.getBoundingClientRect()
    canvas.width = rect.width * dpr
    canvas.height = 160 * dpr
    ctx.scale(dpr, dpr)
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, rect.width, 160)
    ctx.strokeStyle = '#111'
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
  }
  resize()

  let drawing = false
  let lastX = 0
  let lastY = 0

  function getPos(e, canvas) {
    const rect = canvas.getBoundingClientRect()
    if (e.touches) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top }
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  function startDraw(e) {
    e.preventDefault()
    drawing = true
    const pos = getPos(e, canvas)
    lastX = pos.x
    lastY = pos.y
    ctx.beginPath()
    ctx.moveTo(lastX, lastY)
  }

  function draw(e) {
    if (!drawing) return
    e.preventDefault()
    const pos = getPos(e, canvas)
    ctx.lineTo(pos.x, pos.y)
    ctx.stroke()
    lastX = pos.x
    lastY = pos.y
  }

  function stopDraw() { drawing = false }

  canvas.addEventListener('mousedown', startDraw)
  canvas.addEventListener('mousemove', draw)
  canvas.addEventListener('mouseup', stopDraw)
  canvas.addEventListener('mouseleave', stopDraw)
  canvas.addEventListener('touchstart', startDraw, { passive: false })
  canvas.addEventListener('touchmove', draw, { passive: false })
  canvas.addEventListener('touchend', stopDraw)

  return {
    clear() {
      const dpr = window.devicePixelRatio || 1
      const rect = wrapper.getBoundingClientRect()
      ctx.clearRect(0, 0, rect.width * dpr, 160 * dpr)
      ctx.fillStyle = '#fff'
      ctx.fillRect(0, 0, rect.width, 160)
    },
    isEmpty() {
      if (canvas.width === 0 || canvas.height === 0) return true
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data
      for (let i = 0; i < data.length; i += 4) {
        if (data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250) return false
      }
      return true
    },
    toDataURL() { return canvas.toDataURL('image/png') }
  }
}

let mechanicCanvasCtrl = null
let clientCanvasCtrl = null

function openSignatureModal() {
  mechanicSigDataUrl = null
  clientSigDataUrl = null

  document.getElementById('signatureStep1').style.display = 'block'
  document.getElementById('signatureStep2').style.display = 'none'
  document.getElementById('signatureStep3').style.display = 'none'
  document.getElementById('signatureModal').style.display = 'flex'

  updateTranslations()

  // Init mechanic canvas after modal is visible; client canvas init deferred to step 2
  setTimeout(() => {
    mechanicCanvasCtrl = initSignatureCanvas(document.getElementById('mechanicCanvas'))
    clientCanvasCtrl = null
  }, 50)
}

function closeSignatureModal() {
  document.getElementById('signatureModal').style.display = 'none'
  pendingActData = null
}

function maybeReturnToAdmin() {
  if (sessionStorage.getItem('kixik_admin_return')) {
    sessionStorage.removeItem('kixik_admin_return')
    setTimeout(() => { window.location.href = '/equipment.html' }, 1200)
  }
}

// Skip act entirely
document.getElementById('skipActBtn')?.addEventListener('click', () => {
  closeSignatureModal()
  maybeReturnToAdmin()
})

// Mechanic done → go to client step (init client canvas now that step2 is visible)
document.getElementById('toClientSignatureBtn')?.addEventListener('click', () => {
  if (mechanicCanvasCtrl) mechanicSigDataUrl = mechanicCanvasCtrl.toDataURL()
  document.getElementById('signatureStep1').style.display = 'none'
  document.getElementById('signatureStep2').style.display = 'block'
  setTimeout(() => {
    clientCanvasCtrl = initSignatureCanvas(document.getElementById('clientCanvas'))
  }, 50)
})

// Back to mechanic step
document.getElementById('backToMechanicBtn')?.addEventListener('click', () => {
  document.getElementById('signatureStep2').style.display = 'none'
  document.getElementById('signatureStep1').style.display = 'block'
})

// Clear buttons
document.getElementById('clearMechanicCanvas')?.addEventListener('click', () => {
  mechanicCanvasCtrl?.clear()
})
document.getElementById('clearClientCanvas')?.addEventListener('click', () => {
  clientCanvasCtrl?.clear()
})

// Skip client signature → generate without client sig
document.getElementById('skipClientSignatureBtn')?.addEventListener('click', async () => {
  clientSigDataUrl = null
  await runActGeneration()
})

// Generate act with both signatures
document.getElementById('generateActBtn')?.addEventListener('click', async () => {
  if (clientCanvasCtrl && !clientCanvasCtrl.isEmpty()) {
    clientSigDataUrl = clientCanvasCtrl.toDataURL()
  }
  await runActGeneration()
})

async function runActGeneration() {
  if (!pendingActData) return

  document.getElementById('signatureStep2').style.display = 'none'
  document.getElementById('signatureStep1').style.display = 'none'
  document.getElementById('signatureStep3').style.display = 'block'

  try {
    const url = await generateAndUploadAct(pendingActData.id, pendingActData.workLog)
    closeSignatureModal()
    // Reload history so the act link appears
    await loadWorkHistory()
    if (url) {
      if (confirm(t('act.actReadyConfirm'))) {
        window.open(url, '_blank')
      }
    }
    maybeReturnToAdmin()
  } catch (err) {
    console.error('Act generation failed:', err)
    closeSignatureModal()
    alert(t('act.actFailedAlert') + '\n' + err.message)
    maybeReturnToAdmin()
  }
}

// ---- PDF generation ----
function ab2b64(ab) {
  const bytes = new Uint8Array(ab)
  let binary = ''
  const CHUNK = 0x8000
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK))
  }
  return btoa(binary)
}

async function generateAndUploadAct(workLogId, workLog) {
  // Load Roboto variable font (supports Cyrillic + Latin)
  const robotoAB = await fetch('/fonts/Roboto-VariableFont_wdth,wght.ttf').then(r => r.arrayBuffer())

  const doc = new jsPDF({ unit: 'mm', format: 'a4' })

  doc.addFileToVFS('Roboto.ttf', ab2b64(robotoAB))
  doc.addFont('Roboto.ttf', 'Roboto', 'normal')
  doc.addFont('Roboto.ttf', 'Roboto', 'bold')
  doc.setFont('Roboto', 'normal')

  const pw = doc.internal.pageSize.getWidth()
  const ph = doc.internal.pageSize.getHeight()
  const mg = 18
  const cw = pw - 2 * mg
  let y = mg

  // ---- Header: company name + date ----
  const company = currentEquipment.tenant?.company_name || 'KIXIK'
  doc.setFont('Roboto', 'bold')
  doc.setFontSize(13)
  doc.text(company, mg, y)
  doc.setFont('Roboto', 'normal')
  doc.setFontSize(9)
  doc.text(formatDate(workLog.work_date), pw - mg, y, { align: 'right' })
  y += 9

  // ---- Title ----
  doc.setFont('Roboto', 'bold')
  doc.setFontSize(15)
  doc.text(t('act.title'), pw / 2, y, { align: 'center' })
  y += 10

  doc.setDrawColor(180, 180, 180)
  doc.setLineWidth(0.4)
  doc.line(mg, y, pw - mg, y)
  y += 7

  // ---- Info block ----
  doc.setFontSize(10)
  function infoRow(label, value) {
    doc.setFont('Roboto', 'bold')
    doc.text(label + ':', mg, y)
    doc.setFont('Roboto', 'normal')
    const lw = doc.getTextWidth(label + ':') + 4
    const lines = doc.splitTextToSize(String(value || '—'), cw - lw - 2)
    doc.text(lines, mg + lw, y)
    y += lines.length * 5.5 + 1.5
  }

  infoRow(t('act.client'), currentEquipment.client_company || '—')
  infoRow(t('act.equipment'), currentEquipment.name)
  if (currentEquipment.equipment_type?.name) infoRow(t('act.equipmentType'), currentEquipment.equipment_type.name)
  if (currentEquipment.serial_number) infoRow(t('act.serial'), currentEquipment.serial_number)
  const invNumber = currentEquipment.custom_fields?.inv_number
  if (invNumber) infoRow(t('act.inventoryNumber'), invNumber)
  if (currentEquipment.location) infoRow(t('act.location'), currentEquipment.location)
  y += 2

  doc.setLineWidth(0.2)
  doc.line(mg, y, pw - mg, y)
  y += 6

  infoRow(t('act.workType'), t('mechanicPage.' + workLog.work_type))
  infoRow(t('act.workDate'), formatDate(workLog.work_date))
  y += 5

  // ---- Work items table ----
  const workItems = workLog.work_items || []
  if (workItems.length > 0) {
    doc.setFont('Roboto', 'bold')
    doc.setFontSize(10)
    doc.text(t('act.workItemsSection') + ':', mg, y)
    y += 6

    // Column widths: №(10) | Work(~110) | Unit(22) | Hours(cw-142)
    const colNo = 10
    const colHours = 24
    const colUnit = 20
    const colWork = cw - colNo - colUnit - colHours
    const rowH = 8
    const cellPad = 2.5

    function drawTableRow(no, work, unit, hours, isBold, isHeader) {
      if (y + rowH > ph - 55) { doc.addPage(); y = mg }
      const bgColor = isHeader ? [230, 230, 230] : (typeof no === 'number' && no % 2 === 0 ? [248, 248, 248] : [255, 255, 255])
      doc.setFillColor(...bgColor)
      doc.rect(mg, y - rowH + 2, cw, rowH, 'F')
      doc.setDrawColor(200, 200, 200)
      doc.setLineWidth(0.2)
      doc.rect(mg, y - rowH + 2, cw, rowH, 'S')

      doc.setFont('Roboto', isBold ? 'bold' : 'normal')
      doc.setFontSize(9)

      let x = mg + cellPad
      doc.text(String(no), x, y)
      x += colNo
      const workLines = doc.splitTextToSize(String(work), colWork - cellPad * 2)
      doc.text(workLines[0] || '', x, y)
      x += colWork
      doc.text(String(unit), x, y, { align: 'center', maxWidth: colUnit })
      x += colUnit
      doc.text(String(hours), x + colHours - cellPad, y, { align: 'right' })

      if (workLines.length > 1) {
        // Extra height for wrapped text
        const extraH = (workLines.length - 1) * 5
        y += extraH
      }
      y += rowH
    }

    // Header row
    drawTableRow(
      t('act.tableNo'),
      t('act.tableWork'),
      t('act.tableUnit'),
      t('act.tableHours'),
      true, true
    )

    // Data rows
    let totalHours = 0
    workItems.forEach((item, i) => {
      const hrs = item.hours != null ? item.hours : '—'
      if (typeof item.hours === 'number') totalHours += item.hours
      drawTableRow(i + 1, item.name, t('act.tableUnitValue'), String(hrs), false, false)
    })

    // Total row
    doc.setFont('Roboto', 'bold')
    const totalY = y
    if (totalY + rowH > ph - 55) { doc.addPage(); y = mg }
    doc.setFillColor(230, 230, 230)
    doc.rect(mg, y - rowH + 2, cw, rowH, 'F')
    doc.rect(mg, y - rowH + 2, cw, rowH, 'S')
    doc.setFontSize(9)
    const totalLabel = t('act.tableTotal') + ':'
    doc.text(totalLabel, mg + cellPad + colNo + colWork + colUnit - cellPad, y, { align: 'right' })
    doc.text(String(totalHours || '—'), mg + cw - cellPad, y, { align: 'right' })
    y += rowH + 5
  }

  // ---- Notes / description ----
  if (workLog.description) {
    if (y > ph - 70) { doc.addPage(); y = mg }
    doc.setFont('Roboto', 'bold')
    doc.setFontSize(10)
    doc.text(t('act.notes') + ':', mg, y)
    y += 6
    doc.setFont('Roboto', 'normal')
    const descLines = doc.splitTextToSize(workLog.description, cw)
    doc.text(descLines, mg, y)
    y += descLines.length * 5.5 + 6
  }

  // ---- Checklist ----
  if (workLog.checklist_data) {
    try {
      const items = JSON.parse(workLog.checklist_data)
      if (items && items.length > 0) {
        if (y > ph - 60) { doc.addPage(); y = mg }
        doc.setFont('Roboto', 'bold')
        doc.setFontSize(10)
        doc.text(t('act.checklist') + ':', mg, y)
        y += 6
        doc.setFont('Roboto', 'normal')
        items.forEach(item => {
          if (y > ph - 50) { doc.addPage(); y = mg }
          doc.text('\u2713 ' + item, mg + 4, y)
          y += 5.5
        })
        y += 5
      }
    } catch (e) {}
  }

  // ---- Signatures ----
  if (y > ph - 58) { doc.addPage(); y = mg }

  const sigTop = Math.max(y + 4, ph - 55)
  y = sigTop

  doc.setDrawColor(180, 180, 180)
  doc.setLineWidth(0.3)
  doc.line(mg, y, pw - mg, y)
  y += 7

  const halfW = (cw - 10) / 2
  const sigH = 28

  doc.setFont('Roboto', 'bold')
  doc.setFontSize(9)
  doc.text(t('act.mechanicSignature'), mg, y)
  doc.text(t('act.clientSignature'), mg + halfW + 10, y)
  y += 3

  if (mechanicSigDataUrl && !mechanicCanvasCtrl?.isEmpty()) {
    doc.addImage(mechanicSigDataUrl, 'PNG', mg, y, halfW, sigH)
  }
  if (clientSigDataUrl) {
    doc.addImage(clientSigDataUrl, 'PNG', mg + halfW + 10, y, halfW, sigH)
  }
  y += sigH + 3

  doc.setLineWidth(0.3)
  doc.line(mg, y, mg + halfW, y)
  doc.line(mg + halfW + 10, y, pw - mg, y)
  y += 5

  doc.setFont('Roboto', 'normal')
  doc.setFontSize(9)
  doc.text(workLog.mechanic_name || '', mg, y)

  // ---- Upload to Supabase Storage ----
  const pdfBlob = doc.output('blob')
  const filePath = `${currentEquipment.tenant_id}/${workLogId}.pdf`

  const { error: uploadErr } = await supabase.storage
    .from('acts')
    .upload(filePath, pdfBlob, { contentType: 'application/pdf', upsert: true })

  if (uploadErr) throw uploadErr

  const { data: { publicUrl } } = supabase.storage
    .from('acts')
    .getPublicUrl(filePath)

  await supabase
    .from('service_history')
    .update({ act_pdf_url: publicUrl })
    .eq('id', workLogId)

  return publicUrl
}

// ==================== START ====================
document.addEventListener('DOMContentLoaded', init)
// ==================== REQUEST SERVICE ====================
const workLogDetailModal = document.getElementById('workLogDetailModal')
document.getElementById('closeWorkLogDetail')?.addEventListener('click', () => {
  workLogDetailModal.style.display = 'none'
})
workLogDetailModal?.querySelector('.modal-overlay')?.addEventListener('click', () => {
  workLogDetailModal.style.display = 'none'
})

const requestServiceModal = document.getElementById('requestServiceModal')
let selectedPhotos = []
const requestServiceForm = document.getElementById('requestServiceForm')
const requestServiceError = document.getElementById('requestServiceError')
const requestBtnText = document.getElementById('requestBtnText')

document.getElementById('requestServiceBtn')?.addEventListener('click', () => {
  requestServiceModal.style.display = 'flex'
  requestServiceForm.reset()
  selectedPhotos = []
  document.getElementById('photoPreview').innerHTML = ''
  requestServiceError.classList.remove('show')
})

document.getElementById('closeRequestModal')?.addEventListener('click', () => {
  requestServiceModal.style.display = 'none'
})

document.getElementById('cancelRequestBtn')?.addEventListener('click', () => {
  requestServiceModal.style.display = 'none'
})

requestServiceModal?.querySelector('.modal-overlay')?.addEventListener('click', () => {
  requestServiceModal.style.display = 'none'
})

// Превью фото
function handlePhotoFiles(files) {
  const newFiles = Array.from(files)
  selectedPhotos = [...selectedPhotos, ...newFiles].slice(0, 3)
  
  const preview = document.getElementById('photoPreview')
  preview.innerHTML = ''
  selectedPhotos.forEach(file => {
    const reader = new FileReader()
    reader.onload = (ev) => {
      const img = document.createElement('img')
      img.src = ev.target.result
      preview.appendChild(img)
    }
    reader.readAsDataURL(file)
  })
}

document.getElementById('problemPhotos')?.addEventListener('change', (e) => {
  handlePhotoFiles(e.target.files)
})

document.getElementById('problemPhotosCamera')?.addEventListener('change', (e) => {
  handlePhotoFiles(e.target.files)
})
  


// Отправка заявки
requestServiceForm?.addEventListener('submit', async (e) => {
  e.preventDefault()

  const requesterName = document.getElementById('requesterName').value
  const requesterContact = document.getElementById('requesterContact').value
  const problemDescription = document.getElementById('problemDescription').value
  const photoFiles = selectedPhotos.slice(0, 3)

  requestBtnText.textContent = t('mechanicPage.sendingText')
  requestServiceForm.querySelector('button[type="submit"]').disabled = true
  requestServiceError.classList.remove('show')

  try {
    // Конвертируем фото в base64
    const photos = await Promise.all(photoFiles.map(file => {
      return new Promise((resolve) => {
        const img = new Image()
        const reader = new FileReader()
        reader.onload = (ev) => {
          img.onload = () => {
            const canvas = document.createElement('canvas')
            let w = img.width
            let h = img.height
            const maxSize = 1200
            if (w > maxSize || h > maxSize) {
              if (w > h) { h = Math.round(h * maxSize / w); w = maxSize }
              else { w = Math.round(w * maxSize / h); h = maxSize }
            }
            canvas.width = w
            canvas.height = h
            canvas.getContext('2d').drawImage(img, 0, 0, w, h)
            // Сжимаем качество пока файл не меньше 250кб
            let quality = 0.8
            let data = canvas.toDataURL('image/jpeg', quality)
            while (data.length > 250 * 1024 * 1.37 && quality > 0.2) {
              quality -= 0.1
              data = canvas.toDataURL('image/jpeg', quality)
            }
            resolve({ name: file.name, data })
          }
          img.src = ev.target.result
        }
        reader.readAsDataURL(file)
      })
    }))

    // Show which client site this is for, not the maintenance company's
    // own name — KIXIK tenants that service several client sites set
    // client_company per equipment; single-site tenants leave it empty.
    const displayCompany = currentEquipment.client_company || currentEquipment.tenant?.company_name || '—'

    // Сохраняем заявку в базу
    const { error: insertError } = await supabase
      .from('service_requests')
      .insert([{
        tenant_id: currentEquipment.tenant_id,
        equipment_id: currentEquipment.id,
        equipment_name: currentEquipment.name,
        equipment_location: currentEquipment.location || '—',
        client_company: currentEquipment.client_company || null,
        requester_name: requesterName,
        requester_contact: requesterContact,
        problem_description: problemDescription,
        status: 'new'
      }])

    if (insertError) throw insertError

    const emailBody = {
      equipment_name: currentEquipment.name,
      equipment_location: currentEquipment.location || '—',
      company_name: displayCompany,
      requester_name: requesterName,
      requester_contact: requesterContact,
      problem_description: problemDescription,
      photos: photos
    }

    // Send to admin
    const { error } = await supabase.functions.invoke('send-service-request-email', {
      body: { ...emailBody, admin_email: currentEquipment.tenant?.admin_email, specialist_email: null, specialist_name: null }
    })

    if (error) throw error

    const telegramText = `🔧 *New service request*\n\n${currentEquipment.name} — ${displayCompany}\nLocation: ${currentEquipment.location || '—'}\n\nReported by: ${requesterName}\n\n${problemDescription}`

    supabase.functions.invoke('send-telegram-notification', {
      body: { tenant_id: currentEquipment.tenant_id, roles: ['admin'], text: telegramText }
    }).catch(err => console.warn('Telegram admin notification failed:', err))

    if (currentEquipment.assigned_specialist_id) {
      supabase.functions.invoke('send-telegram-notification', {
        body: { user_ids: [currentEquipment.assigned_specialist_id], text: telegramText }
      }).catch(err => console.warn('Telegram specialist notification failed:', err))
    }

    // Send to assigned specialist — use SECURITY DEFINER RPC so it works
    // even without a session token (service requests can be submitted pre-login)
    if (currentEquipment.assigned_specialist_id) {
      try {
        const { data: rows } = await supabase
          .rpc('get_specialist_email_for_equipment', { p_equipment_id: currentEquipment.id })
        const specialist = rows?.[0]
        const specialistEmail = specialist?.email
        const adminEmail = currentEquipment.tenant?.admin_email
        if (specialistEmail && specialistEmail !== adminEmail) {
          supabase.functions.invoke('send-service-request-email', {
            body: { ...emailBody, admin_email: specialistEmail, specialist_email: null, specialist_name: specialist.full_name || null }
          }).catch(err => console.warn('Specialist notification failed:', err))
        }
      } catch (err) {
        console.warn('Could not fetch specialist email:', err)
      }
    }

    requestServiceModal.style.display = 'none'
    alert(t('mechanicPage.requestSentAlert'))

  } catch (error) {
    console.error('Error sending request:', error)
    requestServiceError.textContent = t('mechanicPage.requestFailedError') + error.message
    requestServiceError.classList.add('show')
  } finally {
    requestBtnText.textContent = t('mechanicPage.sendRequestText')
    requestServiceForm.querySelector('button[type="submit"]').disabled = false
  }
})

// ==================== KEYBOARD SHORTCUTS ====================
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return
  const signatureModal = document.getElementById('signatureModal')
  if (signatureModal?.style.display === 'flex') { closeSignatureModal(); return }
  if (workLogDetailModal?.style.display === 'flex') { workLogDetailModal.style.display = 'none'; return }
  if (requestServiceModal?.style.display === 'flex') { requestServiceModal.style.display = 'none'; return }
})
